/*
 * Display copy for the event — now a view over what the API resolved at boot, not a
 * second copy of it.
 *
 * These values used to be typed by hand here and this comment used to warn you to keep
 * them in step with the `event` row you were pointed at, "or the masthead will
 * contradict the timeline underneath it". They come from GET /event/current now, so
 * there is nothing left to keep in step, and no second copy: a live build that cannot
 * reach the server renders a notice rather than an event it made up. The values this
 * file used to hold now live in lib/event-config.ts as DEMO_EVENT, used only by the
 * fixtures.
 */
import { nowMs } from '../lib/clock'
import { eventConfig } from '../lib/event-config'

/**
 * A getter, not a constant.
 *
 * Every consumer reads `EVENT.x` inside a render or a handler, which happens after boot
 * has resolved the config — but a plain object literal would have captured the fallback
 * at module-evaluation time and never seen the real event. The proxy keeps all twenty
 * existing call sites unchanged while making each read live.
 */
/*
 * Room-key handover tracking on the Rooms screen and the delegate's Hotel sheet.
 * Built 2026-09-06, switched off on 2026-09-07 at the organisers' request ("no need
 * yet"). The API routes and the key_collected_at column stay; flip this to bring
 * the tick, the filter chip, the header count and the Hotel sheet line back.
 */
export const KEY_COLLECTION = false

export const EVENT = {
  get name() { return eventConfig().name },
  get shortName() { return eventConfig().shortName },
  get organizer() { return eventConfig().organizer },
  get startDate() { return eventConfig().startDate },
  get endDate() { return eventConfig().endDate },
  get venue() { return eventConfig().venue },
  get certificateAttendanceThreshold() { return eventConfig().certificateAttendanceThreshold },
  get soundCheckin() { return eventConfig().soundCheckin },
  /*
   * Derived, because two fields that must agree are one field. "24 - 26 August 2026",
   * with a plain hyphen: the range is read aloud by screen readers and an en dash adds
   * nothing a hyphen does not.
   */
  get dateLabel() { return formatDateRange(eventConfig().startDate, eventConfig().endDate) },
}

const RANGE_DAY = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric' })
const RANGE_MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' })
const RANGE_FULL = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' })

/**
 * "24–26 August 2026", or "30 August – 2 September 2026" when it straddles a month.
 * En dash for a range; a closed one within a month, a spaced one across months.
 * UTC on purpose: these are plain YYYY-MM-DD calendar dates with no time in them, so
 * parsing them in any other zone can move the day.
 */
export function formatDateRange(start: string, end: string): string {
  const a = Date.parse(`${start}T00:00:00Z`)
  const b = Date.parse(`${end}T00:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return `${start} – ${end}`
  const sameMonth = start.slice(0, 7) === end.slice(0, 7)
  return sameMonth
    ? `${RANGE_DAY.format(a)}–${RANGE_DAY.format(b)} ${RANGE_MONTH_YEAR.format(b)}`
    : `${RANGE_FULL.format(a)} – ${RANGE_FULL.format(b)}`
}


/*
 * en-CA formats as YYYY-MM-DD, which sorts and compares as a string and lines up with
 * startDate/endDate above. Pinned to Malaysia like everything else that touches time:
 * the running head has to turn over at midnight in Nilai, not in whatever zone the
 * delegate's phone believes it is in.
 */
const MYT_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kuala_Lumpur',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const MYT_SHORT_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  day: 'numeric',
  month: 'short',
})

/** Today's calendar date in Malaysia, as YYYY-MM-DD. */
export function mytDateKey(now: number = nowMs()): string {
  return MYT_DATE.format(now)
}

/** "26 Aug" in Malaysian local time. */
export function mytShortDate(now: number = nowMs()): string {
  return MYT_SHORT_DATE.format(now)
}

function wholeDaysBetween(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000,
  )
}

export interface EventProgress {
  /** 1-based day of the conference, or null when today falls outside it. */
  day: number | null
  /** How many days the conference runs. */
  total: number
}

/**
 * Where today sits in the run of the event. `day` is null before it opens and after it
 * closes, which is the signal to show the date range instead of a day number — a
 * running head reading "Day 0 of 3" in the week before would be worse than no day at all.
 */
export function eventProgress(now: number = nowMs()): EventProgress {
  const total = wholeDaysBetween(EVENT.startDate, EVENT.endDate) + 1
  const offset = wholeDaysBetween(EVENT.startDate, mytDateKey(now))
  return { day: offset >= 0 && offset < total ? offset + 1 : null, total }
}
